/**
 * MetricChart - SHARED CHART COMPONENT
 *
 * SINGLE SOURCE OF TRUTH for all metric visualizations.
 * All charts in the app MUST use this component.
 *
 * Features:
 * - Auto-configured YAxis domains based on metric type
 * - Consistent styling using design tokens
 * - Built-in formatters for common metrics (sleep, steps, etc.)
 * - Support for line and bar charts
 * - Support for dual Y-axis (e.g., RHR + HRV)
 * - Responsive container
 */

import React from 'react';
import {
  LineChart,
  BarChart,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { COLORS, SPACING, RADIUS, TYPOGRAPHY } from '../../design/tokens';

// ============================================================
// METRIC CONFIGURATION - Single source of truth for all metrics
// ============================================================

export type MetricType =
  | 'heart_rate'
  | 'hrv'
  | 'weight'
  | 'body_fat'
  | 'calories'
  | 'steps'
  | 'sleep'
  | 'sleep_minutes'
  | 'vo2_max'
  | 'score'        // 0-100 scores (readiness, sleep score, etc.)
  | 'percentage'   // percentages
  | 'count'        // generic counts
  | 'currency'     // money
  | 'wellness'     // 1-3 scale (mood, energy, mental clarity, irritability)
  | 'generic';     // fallback

interface MetricConfig {
  domain: [string | number, string | number];
  unit: string;
  formatter: (value: number) => string;
  color: string;
  ticks?: number[];  // Explicit tick values for Y-axis
}

const METRIC_CONFIGS: Record<MetricType, MetricConfig> = {
  heart_rate: {
    domain: ['dataMin - 5', 'dataMax + 5'],
    unit: 'bpm',
    formatter: (v) => `${Math.round(v)}`,
    color: COLORS.status.error,
  },
  hrv: {
    domain: ['dataMin - 5', 'dataMax + 5'],
    unit: 'ms',
    formatter: (v) => `${Math.round(v)}`,
    color: COLORS.status.info,
  },
  weight: {
    domain: ['dataMin - 2', 'dataMax + 2'],
    unit: 'kg',
    formatter: (v) => { const n = Number(v); return !isNaN(n) ? n.toFixed(1) : '-'; },
    color: COLORS.status.success,
  },
  body_fat: {
    domain: ['dataMin - 2', 'dataMax + 2'],
    unit: '%',
    formatter: (v) => { const n = Number(v); return !isNaN(n) ? n.toFixed(1) : '-'; },
    color: COLORS.status.warning,
  },
  calories: {
    domain: ['dataMin - 100', 'dataMax + 100'],
    unit: 'kcal',
    formatter: (v) => Math.round(v).toLocaleString(),
    color: COLORS.status.warning,
  },
  steps: {
    domain: ['dataMin - 1000', 'dataMax + 1000'],
    unit: 'steps',
    formatter: (v) => Math.round(v).toLocaleString(),
    color: COLORS.status.info,
  },
  sleep: {
    // Sleep in decimal hours
    domain: ['dataMin - 0.5', 'dataMax + 0.5'],
    unit: '',
    formatter: (v) => {
      const h = Math.floor(v);
      const m = Math.round((v - h) * 60);
      return m > 0 ? `${h}h ${m}m` : `${h}h`;
    },
    color: COLORS.accent.default,
  },
  sleep_minutes: {
    // Sleep in minutes
    domain: ['dataMin - 30', 'dataMax + 30'],
    unit: '',
    formatter: (v) => {
      const h = Math.floor(v / 60);
      const m = Math.round(v % 60);
      return m > 0 ? `${h}h ${m}m` : `${h}h`;
    },
    color: COLORS.accent.default,
  },
  vo2_max: {
    domain: ['dataMin - 2', 'dataMax + 2'],
    unit: 'ml/kg/min',
    formatter: (v) => { const n = Number(v); return !isNaN(n) ? n.toFixed(1) : '-'; },
    color: COLORS.accent.default,
  },
  score: {
    domain: [0, 100],
    unit: '',
    formatter: (v) => Math.round(v).toString(),
    color: COLORS.status.success,
  },
  percentage: {
    domain: [0, 100],
    unit: '%',
    formatter: (v) => `${Math.round(v)}%`,
    color: COLORS.status.info,
  },
  count: {
    domain: ['dataMin - 5', 'dataMax + 5'],
    unit: '',
    formatter: (v) => Math.round(v).toString(),
    color: COLORS.text.secondary,
  },
  currency: {
    domain: ['dataMin - 100', 'dataMax + 100'],
    unit: '$',
    formatter: (v) => {
      const abs = Math.abs(v);
      const sign = v < 0 ? '-' : '';
      if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
      if (abs >= 10_000) return `${sign}$${(abs / 1_000).toFixed(0)}k`;
      return `${sign}$${abs.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    },
    color: COLORS.status.success,
  },
  wellness: {
    // Fixed 1-3 scale for mood, energy, mental clarity, irritability
    domain: [0.5, 3.5],
    unit: '',
    formatter: (v) => Math.round(v).toString(),
    color: COLORS.accent.default,
    ticks: [1, 2, 3],
  },
  generic: {
    domain: ['dataMin - 5', 'dataMax + 5'],
    unit: '',
    formatter: (v) => { const n = Number(v); return !isNaN(n) ? n.toFixed(1) : '-'; },
    color: COLORS.accent.default,
  },
};

// Helper to get config for a metric type
export const getMetricConfig = (type: MetricType): MetricConfig => {
  return METRIC_CONFIGS[type] || METRIC_CONFIGS.generic;
};

// Helper to format sleep duration (exported for use elsewhere)
export const formatSleepDuration = (decimalHours: number): string => {
  const h = Math.floor(decimalHours);
  const m = Math.round((decimalHours - h) * 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

// Helper to format sleep from minutes
export const formatSleepMinutes = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

// ============================================================
// CHART COMPONENT
// ============================================================

interface DataSeries {
  dataKey: string;
  name: string;
  metricType: MetricType;
  color?: string;
  yAxisId?: 'left' | 'right';
  strokeDasharray?: string;
  chartType?: 'line' | 'bar';  // Override chart type for this series
  hideDots?: boolean;  // Hide dots on line charts
}

interface MetricChartProps {
  data: any[];
  series: DataSeries[];
  xAxisKey?: string;
  type?: 'line' | 'bar';
  height?: number;
  showLegend?: boolean;
  showGrid?: boolean;
  targetValue?: number;
  targetLabel?: string;
  dualAxis?: boolean;
  rightAxisLabel?: string;
  leftAxisLabel?: string;
  customTooltip?: React.FC<any>;
  yDomain?: [number, number];  // Explicit Y-axis domain override
  yTicks?: number[];           // Explicit Y-axis tick values
  yTickFormatter?: (value: number) => string;  // Custom Y-axis tick label formatter
  yReversed?: boolean;         // Reverse Y-axis (high values at bottom)
}

const MetricChart: React.FC<MetricChartProps> = ({
  data,
  series,
  xAxisKey = 'date',
  type = 'line',
  height = 300,
  showLegend = true,
  showGrid = true,
  targetValue,
  targetLabel = 'Target',
  dualAxis = false,
  rightAxisLabel,
  leftAxisLabel,
  customTooltip,
  yDomain,
  yTicks,
  yTickFormatter,
  yReversed = false,
}) => {
  // Guard against empty/invalid data
  if (!data || data.length === 0 || !series || series.length === 0) {
    return (
      <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.text.tertiary }}>
        No data available
      </div>
    );
  }

  // Filter to only valid series (must have dataKey and metricType)
  const validSeries = series.filter(s => s && s.dataKey && s.metricType);
  if (validSeries.length === 0) {
    return (
      <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.text.tertiary }}>
        No data available
      </div>
    );
  }

  // Check if any series has actual data (not all null/undefined/NaN)
  const hasValidData = validSeries.some(s =>
    data.some(d => {
      const val = d[s.dataKey];
      return val !== null && val !== undefined && !Number.isNaN(val);
    })
  );
  if (!hasValidData) {
    return (
      <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.text.tertiary }}>
        No data available
      </div>
    );
  }

  // Get primary series config for the left Y-axis
  // When dualAxis with only one series, use that series' config regardless of intended yAxisId
  const primarySeries = dualAxis && validSeries.length === 1
    ? validSeries[0]  // Only one series, use it for left axis
    : validSeries.find(s => !s.yAxisId || s.yAxisId === 'left') || validSeries[0];
  const primaryConfig = getMetricConfig(primarySeries?.metricType || 'generic');

  // Default tooltip
  const DefaultTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;

    return (
      <div
        style={{
          background: COLORS.bg.panel,
          border: `1px solid ${COLORS.border.default}`,
          borderRadius: RADIUS,
          padding: SPACING[12],
          fontSize: TYPOGRAPHY.size.sm,
        }}
      >
        <p style={{ color: COLORS.text.secondary, marginBottom: SPACING[4] }}>
          {label}
        </p>
        {payload.map((entry: any, index: number) => {
          const seriesConfig = validSeries.find((s) => s.dataKey === entry.dataKey);
          const config = seriesConfig
            ? getMetricConfig(seriesConfig.metricType)
            : primaryConfig;
          const formatted = config.formatter(entry.value);
          const showUnit = config.unit && !formatted.includes(config.unit);
          return (
            <p
              key={index}
              style={{
                color: entry.color,
                fontWeight: 600,
                margin: `${SPACING[4]} 0 0`,
              }}
            >
              {entry.name}: {formatted}{showUnit ? ` ${config.unit}` : ''}
            </p>
          );
        })}
      </div>
    );
  };

  const TooltipComponent = customTooltip || DefaultTooltip;

  // Determine if we need a composed chart (mixed bar and line series)
  const hasMixedTypes = validSeries.some(s => s.chartType && s.chartType !== type);
  const ChartComponent = hasMixedTypes ? ComposedChart : (type === 'bar' ? BarChart : LineChart);
  const DataComponent = type === 'bar' ? Bar : Line;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ChartComponent data={data}>
        {showGrid && (
          <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border.default} />
        )}
        <XAxis
          dataKey={xAxisKey}
          stroke={COLORS.text.tertiary}
          tick={(props: any) => {
            const { x, y, payload } = props;
            return (
              <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} dy={12} textAnchor="end" fill={COLORS.text.tertiary} fontSize={11} transform="rotate(-35)">
                  {payload.value}
                </text>
              </g>
            );
          }}
          height={55}
          interval="preserveStartEnd"
        />

        {/* Primary (left) Y-Axis */}
        <YAxis
          yAxisId={dualAxis ? 'left' : undefined}
          type="number"
          domain={yDomain || primaryConfig.domain}
          stroke={COLORS.text.tertiary}
          style={{ fontSize: TYPOGRAPHY.size.sm }}
          tickFormatter={yTickFormatter || primaryConfig.formatter}
          ticks={yTicks}
          allowDecimals={!yTicks}
          width={yTickFormatter ? 70 : 65}
          reversed={yReversed}
          label={
            leftAxisLabel
              ? {
                  value: leftAxisLabel,
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: TYPOGRAPHY.size.sm },
                }
              : undefined
          }
        />

        {/* Secondary (right) Y-Axis for dual-axis charts */}
        {dualAxis && validSeries.length > 1 && (
          <YAxis
            yAxisId="right"
            orientation="right"
            domain={getMetricConfig(validSeries[1].metricType).domain}
            stroke={COLORS.text.tertiary}
            style={{ fontSize: TYPOGRAPHY.size.sm }}
            tickFormatter={getMetricConfig(validSeries[1].metricType).formatter}
            label={
              rightAxisLabel
                ? {
                    value: rightAxisLabel,
                    angle: 90,
                    position: 'insideRight',
                    style: { fontSize: TYPOGRAPHY.size.sm },
                  }
                : undefined
            }
          />
        )}

        <Tooltip content={<TooltipComponent />} />
        {showLegend && <Legend />}

        {/* Target reference line */}
        {targetValue !== undefined && (
          <ReferenceLine
            y={targetValue}
            yAxisId={dualAxis ? 'left' : undefined}
            stroke={COLORS.status.success}
            strokeDasharray="3 3"
            label={{
              value: targetLabel,
              fill: COLORS.status.success,
              fontSize: 12,
            }}
          />
        )}

        {/* Data series */}
        {validSeries.map((s, index) => {
          const config = getMetricConfig(s.metricType);
          const color = s.color || config.color;

          // Determine yAxisId: only use 'right' if dualAxis is true AND we have multiple series
          // Otherwise, always use 'left' (or undefined if not dualAxis)
          const effectiveYAxisId = dualAxis
            ? (validSeries.length > 1 ? (s.yAxisId || 'left') : 'left')
            : undefined;

          // Use series-specific chartType if set, otherwise fall back to component-level type
          const seriesType = s.chartType || type;

          if (seriesType === 'bar') {
            return (
              <Bar
                key={s.dataKey}
                dataKey={s.dataKey}
                name={s.name}
                fill={color}
                yAxisId={effectiveYAxisId}
                isAnimationActive={false}
              />
            );
          }

          return (
            <Line
              key={s.dataKey}
              type="monotone"
              dataKey={s.dataKey}
              name={s.name}
              stroke={color}
              strokeWidth={2}
              strokeDasharray={s.strokeDasharray}
              dot={s.hideDots || s.strokeDasharray ? false : { fill: color, r: 4 }}
              activeDot={s.hideDots || s.strokeDasharray ? false : { r: 6 }}
              yAxisId={effectiveYAxisId}
              connectNulls
              isAnimationActive={false}
            />
          );
        })}
      </ChartComponent>
    </ResponsiveContainer>
  );
};

export default MetricChart;
